import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import LoginPage from './pages/auth/LoginPage';
import MainLayout from './components/layout/MainLayout';
import DashboardPage from './pages/dashboard/DashboardPage';
import BrandsPage from './pages/brands/BrandsPage';
import PackagesPage from './pages/packages/PackagesPage';
import UsersPage from './pages/users/UsersPage';
import TicketsPage from './pages/tickets/TicketsPage';
import RolesPermissionsPage from './pages/roles/RolesPermissionsPage';
import AuditLogsPage from './pages/audit-logs/AuditLogsPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user, logout } = useAuth();

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-background-dark">
        <div className="text-slate-600 text-lg font-medium">{t('common.loading')}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user && !user.isSystemAdmin) {
    logout();
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-white">
        <div className="text-slate-600 text-lg font-medium">{t('common.loading')}</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

          {/* Protected - Main Layout */}
          <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/packages" element={<PackagesPage />} />
            {/* Users & Access */}
            <Route path="/users" element={<UsersPage />} />
            <Route path="/roles" element={<RolesPermissionsPage />} />
            <Route path="/permissions" element={<Navigate to="/roles" replace />} />
            {/* Support */}
            <Route path="/tickets" element={<TicketsPage />} />
            {/* Other */}
            <Route path="/billing" element={<PlaceholderPage titleKey="sidebar.billing" />} />
            <Route path="/system-logs" element={<AuditLogsPage />} />
            <Route path="/settings" element={<PlaceholderPage titleKey="sidebar.settings" />} />
          </Route>

          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

const PlaceholderPage: React.FC<{ titleKey: string }> = ({ titleKey }) => {
  const { t } = useTranslation();
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-slate-800 text-2xl font-bold">{t(titleKey)}</h2>
        <p className="text-slate-500 text-sm mt-2">{t('common.comingSoon')}</p>
      </div>
    </div>
  );
};

export default App;
